import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import Fuzzyurl from 'fuzzyurl';
import PropTypes from 'prop-types';

import { CButton, CSkeleton } from '@appcues/component-library';
import PublishDropdown from 'components/Common/Publishing/PublishDropdown';
import ButtonWithTooltip from 'components/Common/ButtonWithTooltip';

import {
  INSTALL_MODAL_TEST_MODE,
  PUBLISHING_MODAL,
  FLOW_PUBLISHING_MODAL,
  TEST_FLOW_MODAL,
} from 'constants/globals';
import { ACCOUNT_PERMISSIONS } from 'constants/accountManagement/permissions';
import { STEP_TYPE_IDS, STEP_TYPES } from 'constants/stepTypes';
import { PUBLISH_ACTIONS } from 'constants/modalStatuses';
import { showModal } from 'actions/currentModal';
import { selectAccountMeta } from 'reducers/account/meta';
import { isInstalledAtDomain } from 'utils';
import { getContentApiPostPromise } from 'helpers/content-api';
import getContentAction from 'actions/publishing';
import {
  selectInstalledDomains,
  selectInstalledDomainsSynced,
} from 'reducers/account/installedDomains';

export const PERMISSIONS_TOOLTIP_TXT =
  'Contact a team admin or publisher to publish.';

export function Publishing({
  accountMeta,
  areInstalledDomainsSynced,
  conditions,
  contentStatus,
  disabledTooltipTxt,
  enableTestMode = false,
  existingTestFlowId,
  flowName = '',
  installedDomains,
  isDisabled,
  isPublished,
  lastUpdatedAtTimestamp,
  onAction,
  previewUrl = '',
  publishToApi = getContentApiPostPromise,
  role,
  showModal: show,
  showTestOptions = false,
  stepId,
  stepType,
}) {
  const confirmAction = publishActionType => {
    const publishingModalType =
      stepType === STEP_TYPE_IDS.JOURNEY
        ? FLOW_PUBLISHING_MODAL
        : PUBLISHING_MODAL;

    show(publishingModalType, {
      publishActionType,
      stepId,
      onAction,
      publishToApi,
    });
  };

  const confirmPublishAction = () => {
    confirmAction(PUBLISH_ACTIONS.PUBLISH);
  };

  const confirmUnpublishAction = () => {
    confirmAction(PUBLISH_ACTIONS.UNPUBLISH);
  };

  const showTestFlowModal = () => {
    const stepProperties = STEP_TYPES[stepType] || {};
    const { contentType } = stepProperties;

    show(TEST_FLOW_MODAL, {
      stepId,
      contentType,
      existingTestFlowId,
      previewUrl,
      conditions,
      flowName,
      accountMeta,
    });
  };

  const showInstallModal = () => {
    const { gates, id: accountId } = accountMeta;

    show(INSTALL_MODAL_TEST_MODE, {
      accountId,
      gates,
      stepId,
    });
  };

  const handleTestFlowClick = () => {
    const urlObject = Fuzzyurl.fromString(previewUrl);
    const domain = urlObject.hostname;

    const isInstalledForUrl = isInstalledAtDomain(domain, installedDomains);

    if (isInstalledForUrl) {
      showTestFlowModal();
    } else {
      showInstallModal();
    }
  };

  const { publishedAndDirty } = contentStatus;
  const cannotPublish = role === ACCOUNT_PERMISSIONS.EDITOR || isDisabled;
  const tooltipTxt =
    isDisabled && disabledTooltipTxt
      ? disabledTooltipTxt
      : PERMISSIONS_TOOLTIP_TXT;

  const lastChangedAt = moment(lastUpdatedAtTimestamp).fromNow();

  const publishOptions = [
    {
      label: 'Push Changes',
      value: PUBLISH_ACTIONS.PUBLISH,
      disabled: cannotPublish,
      icon: 'upload',
      altText: 'Push changes option',
    },
    {
      label: 'Discard Changes',
      value: PUBLISH_ACTIONS.REVERT,
      disabled: cannotPublish,
      icon: 'times',
      altText: 'Discard changes option',
    },
    {
      label: 'Unpublish',
      value: PUBLISH_ACTIONS.UNPUBLISH,
      disabled: cannotPublish,
      icon: 'level-down-alt',
      altText: 'Unpublish flow option',
    },
  ];

  const coolTipProps = {
    marginRight: '18px',
  };

  const isTestable = showTestOptions && enableTestMode;

  return (
    <>
      {isTestable &&
        (areInstalledDomainsSynced ? (
          <CButton type="warning" onClick={handleTestFlowClick}>
            Test
          </CButton>
        ) : (
          <CSkeleton width={100} height={40} />
        ))}
      {!isPublished && !publishedAndDirty && (
        <ButtonWithTooltip
          isDisabled={cannotPublish}
          buttonText="Publish"
          tooltipText={tooltipTxt}
          onClick={confirmPublishAction}
          coolTipProps={coolTipProps}
        />
      )}
      {isPublished && !publishedAndDirty && (
        <CButton isDisabled={cannotPublish} onClick={confirmUnpublishAction}>
          Unpublish
        </CButton>
      )}
      {publishedAndDirty && (
        <PublishDropdown
          options={publishOptions}
          confirmAction={confirmAction}
          optionDetail={lastChangedAt}
          publishDisabled={cannotPublish}
          isPublished={isPublished}
          disabledTooltipTxt={tooltipTxt}
        />
      )}
    </>
  );
}

Publishing.propTypes = {
  contentStatus: PropTypes.shape({
    publishedAndDirty: PropTypes.bool,
  }),
  enableTestMode: PropTypes.bool,
  isPublished: PropTypes.bool,
  lastUpdatedAtTimestamp: PropTypes.number,
  showTestOptions: PropTypes.bool,
  stepType: PropTypes.string,
  role: PropTypes.string,
  areInstalledDomainsSynced: PropTypes.bool,
  isDisabled: PropTypes.bool,
  disabledTooltipTxt: PropTypes.string,
  stepId: PropTypes.string,
  installedDomains: PropTypes.arrayOf(PropTypes.string),
  previewUrl: PropTypes.string,
  existingTestFlowId: PropTypes.string,
  flowName: PropTypes.string,
  conditions: PropTypes.object,
  accountMeta: PropTypes.object.isRequired,
  showModal: PropTypes.func.isRequired,
  onAction: PropTypes.func,
  publishToApi: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    accountMeta: selectAccountMeta(state),
    areInstalledDomainsSynced: selectInstalledDomainsSynced(state),
    installedDomains: selectInstalledDomains(state),
  };
}

const mapDispatchToProps = {
  showModal,
  onAction: (actionType, { content_id, content_type }) =>
    getContentAction(actionType, { id: content_id, type: content_type }),
};

export default connect(mapStateToProps, mapDispatchToProps)(Publishing);
